import React, { useContext } from 'react';
import { Link, MaskedLink } from '@hz-design-system/web-ui';

import EnvironmentContext from '@/client/contexts/EnvironmentContext';
import getClientPageQueryObject from '@/client/utils/getClientPageQueryObject';
import pushToRouter from '@/client/utils/pushToRouter';
import trackEvent from '@/client/utils/analytics/trackEvent';

import createLinks from '@/utils/links/createLinks';
import experiments from '@/utils/labs/experiments';
import { generateLinkObject } from '@/utils/links/linkGeneratorHelper';
import shouldMaskLinkFromCrawler from '@/utils/links/shouldMaskLinkFromCrawler';
import CATEGORIES_L1 from '@/constants/categories';
import {
  attributeKeys,
  PRICE_TYPE_LEASE_ATTRIBUTE_VALUE_ID,
  PRICE_TYPE_SALE_ATTRIBUTE_VALUE_ID,
} from '@/constants/attributes/nonStandards';

interface LinkGeneratorProps {
  eventAction?: string;
  eventLabel?: string;
  eventMetric?: string;
  className?: string;
  children: React.ReactNode;
  rangeAttributes?: any[];
  isSingleSelect?: boolean;
  isSelected?: boolean;
  attributes?: { [key: string]: any };
  page?: number;
  categories?: TCategories;
  distance?: {
    postcode?: string;
    distance?: number;
    distanceMeters?: number;
  };
  query?: string;
  pageSize?: number;
  removeAllFilters?: boolean;
  removeAllAttributes?: boolean;
  searchRequestObject?: TSearchRequestObject;
  bypassSpellingSuggestion?: boolean;
  isSuggestedSearch?: boolean;
  preventScroll?: boolean;
  onClick?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
  tabIndex?: number;
  ariaLabel?: string;
  priceTypeIdentifier?: string;
  priceTypeFacet?: TSelectionGroupFacet;
  role?: string;
}

const LinkGenerator = ({
  eventAction,
  eventLabel,
  eventMetric,
  className,
  children,
  searchRequestObject,
  rangeAttributes,
  categories,
  query = '',
  attributes = {},
  page = 1,
  pageSize,
  isSingleSelect,
  isSelected,
  removeAllFilters = false,
  removeAllAttributes = false,
  bypassSpellingSuggestion,
  distance,
  isSuggestedSearch = false,
  preventScroll = false,
  onClick: onClickCallback,
  tabIndex = 0,
  ariaLabel = '',
  priceTypeIdentifier,
  priceTypeFacet,
  role,
}: LinkGeneratorProps) => {
  const {
    labsConfig,
    tenantContext: { tenant },
  } = useContext(EnvironmentContext);
  const { isFetchResultsOnceEnabled, isFetchResultsOnceAllCatEnabled, isFetchResultsHIOnceEnabled, isSortingEnabled } =
    experiments({ labsConfig });
  const isWomenClothingCategory = searchRequestObject?.categories?.l1Category?.id === CATEGORIES_L1.DAMES_KLEREN;
  const isHouseAndFurnitureCategory =
    searchRequestObject?.categories?.l1Category?.id === CATEGORIES_L1.HUIS_EN_INRICHTING;
  const withAllAttributes =
    isFetchResultsOnceAllCatEnabled ||
    (isFetchResultsOnceEnabled && isWomenClothingCategory) ||
    (isFetchResultsHIOnceEnabled && tenant === 'mp' && isHouseAndFurnitureCategory);

  const withAttributesSorting = isSortingEnabled && !isWomenClothingCategory;

  const linkOptions = {
    searchRequestObject,
    ...(query && { query }),
    newAttribute: attributes,
    isSingleSelect: !!isSingleSelect,
    isSelected: !!isSelected,
    rangeAttributes,
    categories,
    removeAllFilters,
    removeAllAttributes,
    page,
    pageSize,
    bypassSpellingSuggestion,
    distance,
    isSuggestedSearch,
    withAllAttributes: false,
    isSortingEnabled: withAttributesSorting,
  };

  const linkObject = generateLinkObject(linkOptions);
  let linkObjectWithAllAttributes;
  let href: string;
  let pageQueryObj: any;

  if (withAllAttributes) {
    linkOptions.withAllAttributes = true;
    linkObjectWithAllAttributes = generateLinkObject(linkOptions);
    href = createLinks(linkObjectWithAllAttributes);
  } else {
    href = createLinks(linkObject);
  }

  const shouldMask = shouldMaskLinkFromCrawler({
    seller: linkObject.seller,
    prioritizedAttributes: linkObject.prioritizedAttributes,
    rangeAttributes: linkObject.rangeAttributes,
  });

  const LinkComponent = shouldMask ? MaskedLink : Link;

  if (withAllAttributes) {
    pageQueryObj = getClientPageQueryObject(linkObjectWithAllAttributes);
  } else {
    pageQueryObj = getClientPageQueryObject(linkObject);
  }

  const selectAllPriceTypes = () => {
    if (!searchRequestObject) return;
    const attributeGroup: TAttributeValue[] = priceTypeFacet?.attributeGroup || [];

    const saleAttr =
      attributeGroup.find((a) => a.attributeValueId === PRICE_TYPE_SALE_ATTRIBUTE_VALUE_ID) ||
      ({ attributeValueId: PRICE_TYPE_SALE_ATTRIBUTE_VALUE_ID } as TAttributeValue);
    const leaseAttr =
      attributeGroup.find((a) => a.attributeValueId === PRICE_TYPE_LEASE_ATTRIBUTE_VALUE_ID) ||
      ({ attributeValueId: PRICE_TYPE_LEASE_ATTRIBUTE_VALUE_ID } as TAttributeValue);

    const nextSearchRequest: TSearchRequestObject = {
      ...searchRequestObject,
      attributes: { ...searchRequestObject.attributes },
    };

    nextSearchRequest.attributes[attributeKeys.PRICE_TYPE] = [saleAttr, leaseAttr];

    const linkOptionsBoth = {
      ...linkOptions,
      searchRequestObject: nextSearchRequest,
      attributes: nextSearchRequest.attributes,
      newAttribute: {},
      withAllAttributes: false,
    };

    const linkObjBoth = generateLinkObject(linkOptionsBoth);
    const hrefBoth = createLinks(linkObjBoth);
    const pageQueryBoth = getClientPageQueryObject(linkObjBoth);

    pushToRouter({ query: pageQueryBoth, href: hrefBoth });
  };

  const onClickHandler = (event: React.MouseEvent<HTMLAnchorElement>) => {
    trackEvent({ eventAction, eventLabel, eventMetric });

    if (onClickCallback) {
      onClickCallback(event);
    }

    if (preventScroll) {
      if (typeof window !== 'undefined') {
        (window as any).preventScroll = true;
      }
    }

    if (href !== '/') {
      event.preventDefault();
      if (priceTypeIdentifier === `attrchip-${PRICE_TYPE_SALE_ATTRIBUTE_VALUE_ID}`) {
        selectAllPriceTypes();
        return;
      }

      pushToRouter({ query: pageQueryObj, href });
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLAnchorElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      onClickHandler(event as unknown as React.MouseEvent<HTMLAnchorElement>);
    }
  };

  return (
    <LinkComponent
      onClick={onClickHandler}
      href={href}
      className={className}
      tabIndex={tabIndex}
      onKeyDown={handleKeyDown}
      {...(ariaLabel ? { 'aria-label': ariaLabel } : {})}
      {...(role ? { role } : {})}
    >
      {children}
    </LinkComponent>
  );
};

export default LinkGenerator;
